/**
 * A6 — render-time derived section content when primary fields are empty.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';

export type ReportDerivedFallbackFn = (report: SiteRagReport) => string;

export const INSUFFICIENT_RISK_MATRIX_MESSAGE =
  'Insufficient input data to render the risk matrix. See Critical Risks section above.';

export const GENERIC_SECTION_INSUFFICIENT_MESSAGE =
  'This section requires inputs that were not surfaced in the SED. See related sections above for context.';

type NormalizedRisk = { severity: string; title: string; description: string };

/** Collect risks from report.risks and report.developmentRisks. */
export function collectNormalizedReportRisks(report: SiteRagReport): NormalizedRisk[] {
  const items: NormalizedRisk[] = [];
  for (const r of report.risks ?? []) {
    const row = r as typeof r & { title?: string; description?: string };
    items.push({
      severity: row.severity ?? 'medium',
      title: row.label ?? row.title ?? '',
      description: row.impact || row.description || row.mitigation || '',
    });
  }
  for (const r of report.developmentRisks ?? []) {
    items.push({
      severity: (r.riskLevel ?? 'Medium').toLowerCase(),
      title: r.category ?? '',
      description: r.reason || r.action || '',
    });
  }
  return items;
}

export function deriveRiskMatrix(report: SiteRagReport): string | null {
  const risks = collectNormalizedReportRisks(report);
  if (risks.length === 0) return null;

  const lines: string[] = [];
  lines.push('| Risk | Severity | Detail |');
  lines.push('|---|---|---|');
  for (const r of risks.slice(0, 8)) {
    const sev = (r.severity ?? 'medium').toUpperCase();
    const title = (r.title ?? '').slice(0, 80);
    const detail = (r.description ?? '').slice(0, 140);
    lines.push(`| ${title} | ${sev} | ${detail} |`);
  }
  return lines.join('\n');
}

/* ────────────────────────────────────────────────────────────────────────
 * P3 — per-section DETERMINISTIC SKELETONS for the STRUCTURED-field gap
 * sections.
 *
 * The SNT-5 string-field fallback registry (`applySectionFallbacks`) only
 * composes NARRATIVE STRING fields; the sections below read STRUCTURED
 * object/array fields (report.airQualityEmissions, report.permittingPathway,
 * report.lcoeDetailedAnalysis, …) which that loop SKIPS by design — so they
 * only ever showed the GENERIC empty box. These composers run at RENDER time
 * through SectionShell.resolveSectionEmptyMessage and return a short,
 * data-grounded skeleton DERIVED from whatever IS present on the report
 * (what is known + what is pending), NEVER a generic 'pending re-run'.
 *
 * TRUTH-BAR DISCIPLINE (report-quality north star): a composer states the
 * deterministic methodology and the standing diligence gate; it NEVER
 * fabricates an engineering value, and where the report carries no grounding
 * it says a "site-specific assessment is the pending item" in PAST/declarative
 * voice. All composers are PURE (no Date.now/random → deterministic) and
 * phrased to clear the R6 instruction-shape guard (detectInstructionShape):
 * declarative, no "should/must cite/reference …" directives.
 * ──────────────────────────────────────────────────────────────────────── */

/** Join non-empty skeleton sentences into one paragraph (pure). */
function skeleton(...parts: Array<string | null | undefined>): string {
  return parts.map((p) => (p ?? '').trim()).filter((p) => p.length > 0).join(' ');
}

/** "Known:" prefix only when at least one grounded fact is present. */
function knownLine(facts: Array<string | null | undefined>): string | null {
  const kept = facts.map((f) => (f ?? '').trim()).filter((f) => f.length > 0);
  if (kept.length === 0) return null;
  return `Known from site evidence: ${kept.join('; ')}.`;
}

/* ── ENVIRONMENTAL ─────────────────────────────────────────────────────── */

function deriveAirQualitySkeleton(report: SiteRagReport): string {
  const aq = report.airQualityEmissions;
  const known = knownLine([
    aq?.attainmentStatus ? `attainment status — ${aq.attainmentStatus}` : null,
    aq?.baselineAqi ? `baseline AQI — ${aq.baselineAqi}` : null,
  ]);
  return skeleton(
    known,
    'A site-specific air-permit assessment is the pending diligence item: criteria-pollutant',
    'attainment, emissions headroom for the on-site firm-generation nameplate (recip / gas / SMR),',
    'and a General Conformity (40 CFR 93) determination are confirmed once the air-quality dataset',
    'and the chosen generation technology are resolved.',
  );
}

function deriveNoiseVisualSkeleton(_report: SiteRagReport): string {
  return skeleton(
    'A site-specific noise and visual-impact assessment is the pending diligence item:',
    'construction and operational generator-set acoustics against the nearest receptor setbacks,',
    'and visual screening from adjacent parcels, are quantified once parcel adjacency and the',
    'generation-pad layout are resolved.',
  );
}

function deriveMitigationSkeleton(report: SiteRagReport): string {
  const risks = collectNormalizedReportRisks(report);
  if (risks.length > 0) {
    const lines: string[] = [];
    lines.push('Mitigation pathway derived from the identified development risks (detailed measures pending):');
    lines.push('');
    lines.push('| Impact | Indicative mitigation | Status |');
    lines.push('|---|---|---|');
    for (const r of risks.slice(0, 6)) {
      const impact = (r.title || 'Development risk').slice(0, 60);
      const detail = (r.description || 'Site-specific measure to be defined').slice(0, 90);
      lines.push(`| ${impact} | ${detail} | Assessment pending |`);
    }
    return lines.join('\n');
  }
  return skeleton(
    'A per-phase mitigation plan is the pending diligence item: indicative measures are derived',
    'from the environmental risk register and the permitting pathway once those are resolved.',
  );
}

function derivePermittingSkeleton(_report: SiteRagReport): string {
  const lines: string[] = [];
  lines.push('Deterministic NEPA-tier permitting skeleton (jurisdiction-specific timelines pending):');
  lines.push('');
  lines.push('| Tier | Indicative instrument | Status |');
  lines.push('|---|---|---|');
  lines.push('| Federal | NEPA review (CatEx / EA / EIS) sized to the federal nexus | Determination pending |');
  lines.push('| State | State environmental review + air / water permits | Scope pending |');
  lines.push('| Local | Zoning / conditional-use + building permits | Scope pending |');
  return lines.join('\n');
}

/* ── TEST-FIT ──────────────────────────────────────────────────────────── */

function deriveTopographySkeleton(report: SiteRagReport): string {
  const land = report.datasets?.land;
  const elevation = typeof land?.elevation === 'number' && Number.isFinite(land.elevation)
    ? `${land.elevation.toFixed(0)} m centroid elevation`
    : null;
  const slope = land?.slope !== undefined ? `${land.slope}% indicative slope` : null;
  const known = knownLine([elevation, slope]);
  return skeleton(
    known,
    'A detailed topographic survey is the pending diligence item: developable grade,',
    'cut/fill balance, and the data-hall plus generation-pad bench layout are confirmed once',
    'a site survey resolves the parcel contours.',
  );
}

function deriveParkingAccessSkeleton(_report: SiteRagReport): string {
  return skeleton(
    'A parking and emergency-access assessment is the pending diligence item: road frontage,',
    'fire-apparatus turning radii, and parking counts against the campus headcount are confirmed',
    'once parcel access and frontage are resolved.',
  );
}

/* ── POWER-AVAILABILITY ────────────────────────────────────────────────── */

function deriveLcoeSkeleton(_report: SiteRagReport): string {
  const lines: string[] = [];
  lines.push('Published industry-baseline LCOE skeleton (site-specific values pending — no figure fabricated):');
  lines.push('');
  lines.push('| Source | Indicative LCOE basis |');
  lines.push('|---|---|');
  lines.push('| On-site gas (CC) | EIA / NREL ATB published nominal LCOE band |');
  lines.push('| On-site nuclear (SMR) | NREL ATB SMR NOAK published band |');
  lines.push('| Solar + storage | Lazard / NREL ATB solar-plus-storage band |');
  lines.push('| Wind + storage | Lazard / NREL ATB wind-plus-storage band |');
  lines.push('');
  lines.push('A site-specific LCOE build (CAPEX/MW, fixed & variable O&M, fuel, capacity factor) replaces these published bands once the generation technology and economics are resolved.');
  return lines.join('\n');
}

function deriveBtmTechSkeleton(_report: SiteRagReport): string {
  return skeleton(
    'A behind-the-meter technology comparison is the pending diligence item: each candidate source',
    '(grid import, gas, recip, SMR, solar+BESS) is graded on firm-capacity ELCC, time-to-power,',
    'capital, land, and carbon once the power mix and economics are resolved.',
  );
}

function deriveGridCapacitySkeleton(_report: SiteRagReport): string {
  return skeleton(
    'A grid-capacity and substation assessment is the pending diligence item: nearby substation',
    'headroom, interconnection-queue position, and import firmness against the declared 24/7 load',
    'are confirmed once the grid dataset and utility study are resolved.',
  );
}

/* ── WATER-RESOURCE ────────────────────────────────────────────────────── */

function deriveConservationSkeleton(_report: SiteRagReport): string {
  return skeleton(
    'A water-conservation pathway is the pending diligence item: dry / hybrid cooling, closed-loop',
    'and zero-liquid-discharge (ZLD) options for the heat-rejection sink, and their PUE trade-off,',
    'are quantified once the cooling intent and water-supply basis are resolved.',
  );
}

function deriveWaterRiskSkeleton(report: SiteRagReport): string {
  return deriveRiskMatrix(report)
    ?? skeleton(
      'A water risk matrix is the pending diligence item: supply adequacy, water-rights seniority,',
      'and drought-curtailment exposure are scored once the water-stress dataset and the cooling',
      'water demand are resolved.',
    );
}

/**
 * P3 — render-time derived SKELETON registry, keyed by (reportType, sectionId).
 * `resolveSectionEmptyMessage` (wired into SectionShell's empty path) surfaces
 * these automatically — provided the section threads `report` into its
 * SectionShell so the resolver has a report to derive from.
 *
 * NOTE: `capacityCreditDeepDive` is intentionally ABSENT from every type here —
 * it already self-derives a non-empty power-basis / ELCC strip from
 * `projectIntentAtGen` (CapacityCreditDeepDiveSection), so it is never an empty
 * box and needs no skeleton. (POLISH-2 scoped the section to feasibility +
 * power-availability only; it no longer renders on financial / exec-summary.)
 */
export const DERIVED_SECTION_FALLBACKS: Partial<
  Record<ReportTypeId, Record<string, ReportDerivedFallbackFn>>
> = {
  // STAGE 5.1 — feasibility absorbed the grid-screening half, so it inherits
  // the same EMPTY-STATE copy power-availability uses. These entries feed
  // `resolveSectionEmptyMessage`: they are the message shown when a section has
  // no content, NOT synthetic content, so an absent grid screen still reads as
  // absent and still records its omission.
  feasibility: {
    gridCapacity: deriveGridCapacitySkeleton,
  },
  environmental: {
    airQualityEmissions: deriveAirQualitySkeleton,
    noiseVisualImpact: deriveNoiseVisualSkeleton,
    mitigationPlan: deriveMitigationSkeleton,
    permittingPathway: derivePermittingSkeleton,
    environmentalRiskMatrix: (report) => deriveRiskMatrix(report) ?? INSUFFICIENT_RISK_MATRIX_MESSAGE,
  },
  'test-fit': {
    topography: deriveTopographySkeleton,
    parkingAccess: deriveParkingAccessSkeleton,
    riskMatrix: (report) => deriveRiskMatrix(report) ?? INSUFFICIENT_RISK_MATRIX_MESSAGE,
  },
  'power-availability': {
    lcoeBreakdown: deriveLcoeSkeleton,
    btmTechComparison: deriveBtmTechSkeleton,
    gridCapacity: deriveGridCapacitySkeleton,
  },
  'water-resource': {
    conservation: deriveConservationSkeleton,
    waterRiskMatrix: deriveWaterRiskSkeleton,
  },
};

export function resolveDerivedSectionFallback(
  reportType: ReportTypeId | undefined,
  sectionId: string | undefined,
  report: SiteRagReport | undefined,
): string | undefined {
  if (!reportType || !sectionId || !report) return undefined;
  const fn = DERIVED_SECTION_FALLBACKS[reportType]?.[sectionId];
  if (!fn) return undefined;
  const text = fn(report).trim();
  return text.length > 0 ? text : undefined;
}

export function resolveSectionEmptyMessage(
  report: SiteRagReport | undefined,
  sectionId: string | undefined,
  explicit?: string,
): string {
  if (explicit?.trim()) return explicit.trim();
  const derived = resolveDerivedSectionFallback(report?.reportType, sectionId, report);
  if (derived) return derived;
  return GENERIC_SECTION_INSUFFICIENT_MESSAGE;
}
